import { ChangeDetectionStrategy, Component, ComponentFactoryResolver, ComponentRef, ElementRef, EmbeddedViewRef, HostListener, Type, ViewContainerRef } from '@angular/core';
import { InjectConfig, IqConfigKey } from '@ddt-web/config-layer';
import { CellData } from '@ddt-web/data-service';
import { SlugifyPipe } from '@ddt-web/generic-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ICellRenderer, ICellRendererParams } from 'ag-grid-community';
import tippy, { hideAll, Instance, Props } from 'tippy.js';

import { FundStatusInteractionMenuComponent } from '../components/fund-status-interaction-menu/fund-status-interaction-menu.component';
import { StatusChange } from '../enums/status-change.enum';
import { StatusInteractionType } from '../models';
import { FundData } from '../models/fund-data.interface';
import { StatusColour } from '../models/status-colour.interface';

@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'fund-stats-status-cell',
	templateUrl: './status-cell.component.html',
	styleUrls: ['./status-cell.component.scss']
})
export class StatusCellRendererComponent implements ICellRenderer {
	@InjectConfig(IqConfigKey.ENABLE_FUND_STATUS_INTERACTIONS) private readonly enableStatusInteractions = false;

	public value: CellData;
	public isSupportedColour: boolean;
	public statusIconSrc: string;

	public statusColour: string;
	public comparisonStatusColour: string;
	public isFundIdea: boolean;
	public isSuggestedAddition;
	public isBuyFund: boolean;
	public isAof: boolean;
	public statusChangeIcon: string;
	public statusChange: StatusChange;
	public statusInteractionId: StatusInteractionType;

	private asOfDate: string;
	private asOfDateCompare: string;
	public fundData: FundData;

	public enableTooltip: boolean = true;
	public tooltipStatusIconSrc: string;
	public tooltipStatusComparisonIconSrc: string;

	public showDropdown: boolean = false;
	public showEditButton: boolean = false;

	private tippyInstance: Instance<Props>;
	private readonly tippyProps: Partial<Props> = {
		animation: 'fade',
		trigger: 'manual',
		placement: 'right-start',
		arrow: false,
		interactive: true,
		showOnCreate: true,
		appendTo: document.body,
		hideOnClick: false,
		onShow: (instance: Instance<Props>) => hideAll({ exclude: instance }),
		onClickOutside: (instance: Instance<Props>) => this.closeMenu(instance)
	};

	public constructor(
		private elementRef: ElementRef,
		private viewContainerRef: ViewContainerRef,
		private componentFactoryResolver: ComponentFactoryResolver
	) { }

	public agInit(params: ICellRendererParams): void {
		this.value = params.value;
		this.fundData = params.data;

		this.enableTooltip = !this.enableStatusInteractions && params['enableTooltip'];

		this.statusChange = this.value?.attribute?.statusChange;
		this.statusInteractionId = this.value?.attribute?.statusInteraction;

		if (this.statusChange === StatusChange.UPGRADE) {
			this.statusChangeIcon = 'arrow-up';
		} else if (this.statusChange === StatusChange.DOWNGRADE) {
			this.statusChangeIcon = 'arrow-down';
		}

		this.statusColour = new SlugifyPipe().transform(this.value?.formattedValue);
		if (this.statusChange) {
			this.comparisonStatusColour = new SlugifyPipe().transform(this.value?.attribute?.comparisonStatus);
		}

		this.isSupportedColour = !!StatusColour.get[this.statusColour];

		// valid for IQ - they will be a boolean flag
		this.isFundIdea = this.value?.attribute?.fundIdea?.isFundIdea;
		this.isSuggestedAddition = this.value?.attribute?.fundIdea?.isSuggestedAddition;

		// valid for IQ
		this.isBuyFund = this.value?.attribute?.isBuyFund;

		// valid for PA - lightbulb datapoint is only populated for idea groups and it contains a string ticker
		const lightbulbTicker = params.data?.lightbulb?.rawValue;
		this.isAof = params.colDef.field !== 'status';

		const isColumnTaxOffender = params.colDef.field === 'highTaxOffenders' || params.colDef.field === 'worstTaxOffenders';
		const isTaxQualifiedFund = this.value?.attribute?.isTaxQualifiedFund;

		if (lightbulbTicker && lightbulbTicker === params.data?.ticker?.rawValue) {
			if (!!this.value.rawValue) {
				this.statusIconSrc = `fte-${this.statusColour}.svg`;
			}
		} else if (this.isBuyFund && !this.isAof) {
			if (this.statusInteractionId === 'dismiss') {
				this.statusIconSrc = `iq/buy-fund-dismiss-${this.statusColour}.svg`;
			} else {
				this.statusIconSrc = `iq/buy-fund-${this.statusColour}.svg`;
			}

			this.tooltipStatusIconSrc = `iq/status-${this.statusColour}-tooltip.svg`;

			if (this.statusChange) {
				this.tooltipStatusComparisonIconSrc = `iq/status-${this.comparisonStatusColour}-tooltip.svg`;
			}
		} else if (this.isFundIdea && !this.isAof) {
			if (!this.isSuggestedAddition && this.statusInteractionId === 'dismiss') {
				this.statusIconSrc = `iq/fund-idea-dismiss-${this.statusColour}.svg`;
			} else if (!this.isSuggestedAddition && this.statusInteractionId === 'accept') {
				this.statusIconSrc = `iq/accept-${this.statusColour}.svg`;
			} else {
				this.statusIconSrc = `iq/fund-idea-${this.statusColour}.svg`;
			}
			this.tooltipStatusIconSrc = `iq/status-${this.statusColour}-tooltip.svg`;

			if (!this.isSuggestedAddition && this.statusChange) {
				this.tooltipStatusComparisonIconSrc = `iq/status-${this.comparisonStatusColour}-tooltip.svg`;
			}
		} else if (this.statusInteractionId === 'accept' && !this.isAof) {
			this.statusIconSrc = `iq/accept-${this.statusColour}.svg`;
		} else if (this.statusInteractionId === 'dismiss' && !this.isAof) {
			this.statusIconSrc = `iq/dismiss-${this.statusColour}.svg`;
		} else if (isTaxQualifiedFund && isColumnTaxOffender) {
			this.statusIconSrc = `iq/status-gray.svg`;
			this.tooltipStatusIconSrc = `iq/status-${this.statusColour}-tooltip.svg`;
		} else {
			this.statusIconSrc = `iq/status-${this.statusColour}.svg`;
			this.tooltipStatusIconSrc = `iq/status-${this.statusColour}-tooltip.svg`;

			if (this.statusChange) {
				this.tooltipStatusComparisonIconSrc = `iq/status-${this.comparisonStatusColour}-tooltip.svg`;
			}
		}

		this.asOfDate = this.value?.attribute?.asOfDate;
		this.asOfDateCompare = this.value?.attribute?.asOfDateCompare;

		params['scroll$']?.pipe(untilDestroyed(this)).subscribe(() => {
			this.onScroll();
		});
	}

	public refresh(): boolean {
		return false;
	}

	@HostListener('window:scroll', ['$event'])
	public onScroll(): void {
		if (this.tippyInstance) {
			this.closeMenu(this.tippyInstance);
		}
	}

	@HostListener('mouseenter')
	public onMouseover(): void {
		this.showEditButton = !this.isAof;
	}

	@HostListener('mouseleave')
	public onMouseleave(): void {
		this.showEditButton = false;
	}

	public togglePopup(): void {
		if (this.enableStatusInteractions && !this.isAof) {
			if (!this.tippyInstance) {
				this.tippyInstance = tippy(this.elementRef.nativeElement, this.tippyProps) as any;
			}

			this.showDropdown = !this.showDropdown;
			if (this.showDropdown) {
				const menuComponent: HTMLElement = this.createComponentElement<FundStatusInteractionMenuComponent>(
					FundStatusInteractionMenuComponent,
					this.viewContainerRef,
					this.componentFactoryResolver,
					{
						showHeader: true,
						fundData: this.fundData,
						isSuggestedAddition: this.isSuggestedAddition,
						displayStatus: true,
						statusColour: this.statusColour,
						comparisonStatusColour: this.comparisonStatusColour,
						statusChange: this.statusChange,
						initialInteractionId: this.statusInteractionId,
						asOfDate: this.asOfDate,
						asOfDateCompare: this.asOfDateCompare,
						closeCallback: () => this.closeMenu(this.tippyInstance)
					}
				);

				// The tippy content needs to be set in a timeout to prevent it being done before the Angular component has been fully initialised
				setTimeout(() => {
					menuComponent.style.display = 'block';
					this.tippyInstance.setContent(menuComponent);
				}, 0);

				this.tippyInstance.enable();
				this.tippyInstance.show();
			} else {
				this.tippyInstance.unmount();
			}
		}
	}

	private closeMenu(instance: Instance<Props>): void {
		if (this.showDropdown) {
			this.showDropdown = false;
			instance.unmount();
		}
	}

	public createComponentElement<T>(
		componentType: Type<T>,
		viewContainerRef: ViewContainerRef,
		componentFactoryResolver: ComponentFactoryResolver,
		params: { [key: string]: any }
	): HTMLElement {
		const componentRef: ComponentRef<T> = viewContainerRef.createComponent(componentFactoryResolver.resolveComponentFactory(componentType));
		const componentInstance: T = componentRef.instance;
		Object.keys(params).forEach((key: string) => {
			componentInstance[key] = params[key];
		});
		const componentElement = (componentRef.hostView as any as EmbeddedViewRef<any>).rootNodes[0];
		componentElement.style.display = 'none'; // The created component will be invisible until needed
		return componentElement;
	}
}
